import { useCallback, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";

// Mirrors ChatGptLinkState on the backend.
export type ChatGptLinkState =
  | "NotStarted"
  | "AwaitingApproval"
  | "Authorized"
  | "Expired"
  | "Failed";

// How much of the ChatGPT plan's allowance is left in the current window.
// resetsLabel is display text as OpenClaw renders it ("6d 23h" or "Aug 10").
export type ChatGptUsage = {
  windowHours: number | null;
  percentLeft: number | null;
  resetsLabel: string | null;
};

export type ChatGptStatus = {
  state: ChatGptLinkState;
  accountEmail: string | null;
  activePrimary: string | null;
  usage: ChatGptUsage | null;
  credentialExpiresAt: string | null;
  // The login in flight, read off the instance's own transcript. Null until the
  // code renders (~40s after startLink). This poll is how the code reaches the
  // customer — startLink deliberately doesn't wait around for it.
  verificationUrl: string | null;
  userCode: string | null;
};

// Installing is NOT an error: the Codex add-on installs in the background on the
// instance (~4.5 min cold), so it's the normal first answer for anyone who has
// never switched before. Keep polling until Ready or Failed.
export type ChatGptPrepareState = "Ready" | "Installing" | "Failed";

// What the instance is ACTUALLY billing to, read from the box (~27s). The
// dashboard's own `chatgptLinked` flag comes from our database, which only
// learns about switches made through this flow — so it reports OpenRouter for
// any instance an operator set up by hand. This is the honest answer, and
// fetching it also repairs the stored one.
export type ChatGptProviderInfo = {
  runningOn: "ChatGpt" | "OpenRouter";
  activePrimary: string | null;
  // The account actually being billed — the head of the instance's auth order,
  // not whichever the profile listing happened to return first.
  accountEmail: string | null;
  // Every ChatGPT account on the instance, default first.
  linkedAccounts: string[];
};

// What an ordering change answers: who is billed now, and the failover order
// behind them.
//
// Deliberately NOT ChatGptProviderInfo. Reordering accounts cannot move a model,
// so runningOn/activePrimary would be unchanged — and answering them would mean
// a third instance round-trip inside a request that already carries a read and a
// config write, which does not fit the 60s edge budget. The caller merges this
// into the provider state it already holds.
export type ChatGptAccountsInfo = {
  accountEmail: string | null;
  linkedAccounts: string[];
};

export type ChatGptPrepare = {
  state: ChatGptPrepareState;
  coreVersion: string | null;
  reason: string | null;
};

// Steady-state panel read, served from the tenant row (no instance round-trip).
export type ChatGptPanel = {
  linked: boolean;
  accountEmail: string | null;
  linkedAt: string | null;
  model: string;
};

export type ChatGptDeviceCode = {
  verificationUrl: string | null;
  userCode: string | null;
};

export type ChatGptErrorCode =
  | "FEATURE_DISABLED"
  | "NOT_LINKED"
  | "PUSH_FAILED"
  | "INVALID_STATE"
  | "CONFIG_REVIEW_REQUIRED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "UNAUTHORIZED"
  // ztc-core answered with a code this build does not know.
  | "UNEXPECTED"
  // Nothing came back from ztc-core (the edge gave up), so the outcome is unknown.
  | "UNKNOWN";

export type ChatGptResult<T> =
  | { ok: true; value: T }
  | { ok: false; code: ChatGptErrorCode; message: string };

export type UseChatGptLinkResult = {
  // True while a USER-INITIATED request is in flight, so the modal can disable
  // its actions without each caller tracking its own boolean. Deliberately not
  // set by refreshStatus: that runs on a 5s poll while the customer is away at
  // OpenAI, and letting it drive `busy` would flicker every button into its
  // loading label twice a minute.
  busy: boolean;
  loadPanel: () => Promise<ChatGptResult<ChatGptPanel>>;
  prepare: () => Promise<ChatGptResult<ChatGptPrepare>>;
  // Same call as prepare, but untracked — it runs on a timer while the Codex
  // add-on installs, and letting it drive `busy` would flicker every button.
  pollPrepare: () => Promise<ChatGptResult<ChatGptPrepare>>;
  // Returns 202 with no body: all it confirms is that the login is running.
  startLink: () => Promise<ChatGptResult<void>>;
  refreshStatus: () => Promise<ChatGptResult<ChatGptStatus>>;
  loadProvider: () => Promise<ChatGptResult<ChatGptProviderInfo>>;
  loadUsage: () => Promise<ChatGptResult<{ usage: ChatGptUsage | null }>>;
  activate: () => Promise<ChatGptResult<ChatGptStatus>>;
  // Promote a connected account to the one that gets billed. Everything else
  // stays behind it as failover.
  setDefaultAccount: (accountEmail: string) => Promise<ChatGptResult<ChatGptAccountsInfo>>;
  // Keep a connected account as a spare, behind the current default. Separate
  // from the above so adding one can never silently change who pays.
  addFallbackAccount: (accountEmail: string) => Promise<ChatGptResult<ChatGptAccountsInfo>>;
};

type ServerError = { code: string; message: string };

const SERVER_CODES: ReadonlyArray<ChatGptErrorCode> = [
  "FEATURE_DISABLED", "NOT_LINKED", "PUSH_FAILED", "INVALID_STATE",
  "CONFIG_REVIEW_REQUIRED", "FORBIDDEN", "NOT_FOUND", "UNAUTHORIZED",
];

// Shown for anything that didn't come from ztc-core itself. Every step of this
// flow now returns in seconds, so this should be rare — but when it happens the
// work on the instance has usually landed regardless, and telling the customer
// "that failed" would send them into a pointless retry.
const EDGE_MESSAGE =
  "We couldn't confirm that with your instance. Give it a minute, then check again before retrying.";

// An error body from ztc-core is JSON with code + message; anything else (an
// HTML error page from CloudFront) parses to null and is treated as an edge
// failure rather than a backend one. An unrecognised code is still ztc-core's
// answer, so it becomes UNEXPECTED: the modal reads UNKNOWN as "the switch may
// still be landing" and would keep polling a refusal.
export const readServerError = (raw: string): { code: ChatGptErrorCode; message: string } | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<ServerError> | null;
    if (parsed === null || typeof parsed.message !== "string") return null;
    const code = SERVER_CODES.find((known) => known === parsed.code) ?? "UNEXPECTED";
    return { code, message: parsed.message };
  } catch {
    return null;
  }
};

// Every step of the ChatGPT-subscription switch. Deliberately NOT a
// useCachedQuery/SWR surface: none of these are cacheable GETs of steady state —
// they drive a short-lived login whose whole point is that it changes under you,
// and a stale-then-revalidate read of "are you linked yet" would show the
// customer a state that has already moved on. The panel's steady-state read
// (linked / not) rides on the instance payload instead.
export const useChatGptLink = (tenantId: string): UseChatGptLinkResult => {
  const [busy, setBusy] = useState<boolean>(false);
  const base = `/api/claws/${encodeURIComponent(tenantId)}/chatgpt`;

  const call = useCallback(
    async <T,>(
      path: string,
      method: "GET" | "POST",
      // Background polls pass false so they never disturb the button states.
      tracked: boolean = true,
      // Named `payload` because `body` is taken further down by the parsed
      // error body, and the shadowing trips TS's use-before-declaration check.
      payload?: unknown,
    ): Promise<ChatGptResult<T>> => {
      if (tracked) setBusy(true);
      try {
        const res = await fetch(`${base}${path}`, {
          method,
          credentials: "include",
          ...(method === "POST"
            ? {
                headers: {
                  ...getCsrfHeader(),
                  ...(payload === undefined ? {} : { "Content-Type": "application/json" }),
                },
                ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
              }
            : {}),
        });
        // Read as text first: an error page from the edge is HTML, and calling
        // res.json() on it throws inside the success path.
        const raw = await res.text().catch(() => "");
        if (res.ok) {
          // 202 (startLink) has no body by design.
          return { ok: true, value: (raw === "" ? undefined : JSON.parse(raw)) as T };
        }
        if (res.status === 401) {
          return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
        }
        const body = readServerError(raw);
        if (body !== null) {
          return { ok: false, ...body };
        }
        // No JSON body means this did not come from ztc-core — it's CloudFront
        // or the ALB (a 504 on a slow origin, a 502/503 mid-deploy). The
        // instance-side work usually lands anyway, so don't imply it didn't.
        return { ok: false, code: "UNKNOWN", message: EDGE_MESSAGE };
      } catch {
        return { ok: false, code: "UNKNOWN", message: EDGE_MESSAGE };
      } finally {
        if (tracked) setBusy(false);
      }
    },
    [base],
  );

  return {
    busy,
    loadPanel: useCallback(() => call<ChatGptPanel>("", "GET", false), [call]),
    prepare: useCallback(() => call<ChatGptPrepare>("/prepare", "POST"), [call]),
    pollPrepare: useCallback(() => call<ChatGptPrepare>("/prepare", "POST", false), [call]),
    startLink: useCallback(() => call<void>("/link/start", "POST"), [call]),
    refreshStatus: useCallback(() => call<ChatGptStatus>("/link/status", "GET", false), [call]),
    loadProvider: useCallback(() => call<ChatGptProviderInfo>("/provider", "GET", false), [call]),
    loadUsage: useCallback(
      () => call<{ usage: ChatGptUsage | null }>("/usage", "GET", false),
      [call],
    ),
    activate: useCallback(() => call<ChatGptStatus>("/activate", "POST"), [call]),
    setDefaultAccount: useCallback(
      (accountEmail: string) =>
        call<ChatGptAccountsInfo>("/accounts/default", "POST", true, { accountEmail }),
      [call],
    ),
    addFallbackAccount: useCallback(
      (accountEmail: string) =>
        call<ChatGptAccountsInfo>("/accounts/fallback", "POST", true, { accountEmail }),
      [call],
    ),
    // No revert here on purpose. Leaving ChatGPT needs working credentials for
    // the next provider, so it goes through the admin AI-mode switch.
  };
};
